/**
 * 과제 진행률 계산 유틸
 *
 * 진행률 = (word + sentence + video 파트별 진행률)의 평균
 * - setting: Content/setting 또는 배포 시 report_setting 오버라이드
 * - report_permission: 지점별로 활성화된 콘텐츠 타입만 집계
 * - ReportProgress/{student_uid}/{release_id}/{type} 문서의 boolean 필드로 완료 판단
 */
import { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import { report, ContentSettings } from "shared/types/Homework";

export const CONTENT_TYPES = ["word", "sentence", "video"] as const;
export const DEFAULT_REPORT_PERMISSION = { word: false, sentence: false, video: false };

type FirestoreDoc = {
    exists: () => boolean;
    data: () => DocumentData | undefined;
};

type ReadFirestoreOneDoc = (collectionPath: string, docId: string) => Promise<FirestoreDoc>;
type ReadFirestore = (collectionPath: string) => Promise<{
    docs: QueryDocumentSnapshot<DocumentData>[];
    empty: boolean;
}>;

export type ReportProgressContext = {
    currentSetting: ContentSettings;
    activeContentTypes: string[];
};

export const checkContentExistence = async (
    readFirestoreOneDoc: ReadFirestoreOneDoc,
    reportPath: string,
    reportId: string
): Promise<Record<string, boolean>> => {
    const existenceChecks = CONTENT_TYPES.map(async (type) => {
        const exists = await readFirestoreOneDoc(`${reportPath}/${reportId}/Content`, type).then(
            (res) => res.exists()
        );
        return { type, exists };
    });

    const results = await Promise.all(existenceChecks);
    return Object.fromEntries(results.map(({ type, exists }) => [type, exists]));
};

/** Content 하위에 실제 word/sentence/video 문서가 없으면 해당 setting을 비활성화 */
export const adjustSettingsForMissingContent = (
    settings: ContentSettings | null | undefined,
    contentExistence: Record<string, boolean>
): ContentSettings | null | undefined => {
    if (!settings) return settings;

    const adjustedSettings = { ...settings };

    if (!contentExistence.word && adjustedSettings.word) {
        adjustedSettings.word = { ...adjustedSettings.word, focus: false, write: false, painting: false };
    }

    if (!contentExistence.sentence && adjustedSettings.sentence) {
        adjustedSettings.sentence = {
            ...adjustedSettings.sentence,
            listening: false,
            order: false,
            record: false,
        };
    }

    if (!contentExistence.video && adjustedSettings.video) {
        adjustedSettings.video = { ...adjustedSettings.video, video: false };
    }

    return adjustedSettings;
};

/** 단일 파트(word|sentence|video) 진행률: setting에서 true인 항목 대비 ReportProgress 완료 비율 */
const calculatePartProgress = async (
    readFirestoreOneDoc: ReadFirestoreOneDoc,
    releaseId: string,
    settings: ContentSettings,
    type: string,
    studentUid: string
): Promise<number> => {
    try {
        const progressDoc = await readFirestoreOneDoc(`ReportProgress/${studentUid}/${releaseId}`, type);
        const targetProgress = progressDoc.exists() ? progressDoc.data() : null;
        const targetSetting = settings[type];
        if (!targetSetting) return 0;

        const settingKeys = Object.keys(targetSetting).filter(
            (key) => typeof targetSetting[key] === "boolean" && targetSetting[key]
        );
        const totalCount = settingKeys.length;

        if (totalCount === 0 && !targetProgress) return 100;
        if (!targetProgress) return 0;

        const completeCount = settingKeys.filter((key) => targetProgress[key] === true).length;
        return Math.ceil((completeCount / totalCount) * 100) || 0;
    } catch (error) {
        console.error("Error calculating progress:", error);
        return 0;
    }
};

/**
 * 과제 공통 데이터를 한 번만 조회 (학생 N명 조회 시 반복 호출 방지)
 * setting, branch permission, Content 목록을 병렬로 가져옴
 */
export const buildReportProgressContext = async (
    deps: {
        readFirestoreOneDoc: ReadFirestoreOneDoc;
        readFirestore: ReadFirestore;
    },
    reportData: Pick<report, "id" | "path">,
    reportSetting: ContentSettings | null | undefined,
    branchId: string
): Promise<ReportProgressContext> => {
    const contentPath = `${reportData.path}/${reportData.id}/Content`;

    const [setting, branchDoc, contentQuery] = await Promise.all([
        reportSetting != null
            ? Promise.resolve(reportSetting)
            : deps.readFirestoreOneDoc(contentPath, "setting").then(
                  (res) => (res.data() ?? {}) as ContentSettings
              ),
        deps.readFirestoreOneDoc("User/Auth/Branch", branchId),
        deps.readFirestore(contentPath),
    ]);

    const contentExistence = Object.fromEntries(
        CONTENT_TYPES.map((type) => [type, contentQuery.docs.some((doc) => doc.id === type)])
    );
    const currentSetting = adjustSettingsForMissingContent(setting, contentExistence) as ContentSettings;

    const reportPermission = branchDoc.exists()
        ? branchDoc.data()?.report_permission ?? DEFAULT_REPORT_PERMISSION
        : DEFAULT_REPORT_PERMISSION;

    const activeContentTypes = contentQuery.docs
        .filter((doc) => doc.id !== "setting" && reportPermission[doc.id as keyof typeof reportPermission])
        .filter((doc) => {
            const typeSetting = currentSetting[doc.id];
            if (typeSetting) {
                return Object.keys(typeSetting).some(
                    (key) => typeof typeSetting[key] === "boolean" && typeSetting[key]
                );
            }
            return true;
        })
        .map((doc) => doc.id);

    return { currentSetting, activeContentTypes };
};

/** buildReportProgressContext 결과를 재사용해 학생별 진행률만 조회 */
export const calculateReportProgressWithContext = async (
    readFirestoreOneDoc: ReadFirestoreOneDoc,
    releaseId: string,
    studentUid: string,
    context: ReportProgressContext
): Promise<number> => {
    const { currentSetting, activeContentTypes } = context;

    if (activeContentTypes.length === 0) return 0;

    const contentProgress = await Promise.all(
        activeContentTypes.map((type) =>
            calculatePartProgress(readFirestoreOneDoc, releaseId, currentSetting, type, studentUid)
        )
    );

    const totalProgress = contentProgress.reduce((sum, progress) => sum + progress, 0);
    return Math.ceil(totalProgress / contentProgress.length) || 0;
};

export const calculateReportProgress = async (
    deps: {
        readFirestoreOneDoc: ReadFirestoreOneDoc;
        readFirestore: ReadFirestore;
    },
    reportData: Pick<report, "id" | "path" | "release_id">,
    studentUid: string,
    reportSetting: ContentSettings | null | undefined,
    branchId: string
): Promise<number> => {
    try {
        const context = await buildReportProgressContext(
            deps,
            reportData,
            reportSetting,
            branchId
        );
        return calculateReportProgressWithContext(
            deps.readFirestoreOneDoc,
            reportData.release_id,
            studentUid,
            context
        );
    } catch (error) {
        console.error("Error calculating report progress:", error);
        return 0;
    }
};
